// Pestaña Producto: ficha del catálogo, «Editar» y «Desactivar» con confirmación (retail.product.update).
import { useState, type ReactNode } from 'react';
import { Pencil, Power } from 'lucide-react';
import { toast } from 'sonner';
import { ProductStatusEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductStatusEnum';
import { Button, ConfirmDialog, ErrorState, Skeleton } from '@/kit';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import { cn } from '@/lib/cn';
import { useIdempotentAttempt } from '@/hooks/useIdempotentAttempt';
import { useCatalogProduct, useDeactivateProduct } from '../../api/useCatalogWrites';
import {
    FINANCIER_OPTIONS,
    MDM_LOCK_MODE_OPTIONS,
    MDM_PROVIDER_OPTIONS,
    PRODUCT_CATEGORY_OPTIONS,
    TIER_FORM_OPTIONS,
    itemTypeOf,
    type CatalogItemKind,
} from '../../lib/catalogForms';
import { skuDetailErrorMessage } from '../../lib/inventoryDetail';
import { useInventoryAbilities } from '../../lib/inventoryPermissions';
import { deactivateErrorOf } from '../../lib/inventoryWriteErrors';
import { EditItemDrawer, trackingLabel } from '../catalog/EditItemDrawer';
import { WriteErrorNotice } from '../catalog/FormBits';
import { DetailEmpty } from './DetailCard';
import { useIntegrationLock } from '../../../integration/components/IntegrationLock';
import { isIntegrationFreeKind } from '../../../integration/lib/integrationLock';
import { variantLabel } from '@/shared/lib/format/capacity';

const KIND_LABEL: Record<CatalogItemKind, string> = {
    PRODUCT: 'Producto',
    KIT: 'Kit',
    SERVICE: 'Servicio',
};

function labelOf(
    options: readonly { value: string; label: string }[],
    value: string | null | undefined,
) {
    return options.find((o) => o.value === (value ?? ''))?.label ?? '—';
}

const yesNo = (value: boolean | undefined) => (value ? 'Sí' : 'No');

function Field({
    label,
    wide = false,
    children,
}: {
    label: string;
    /** A lo ancho de la rejilla: para los textos largos. */
    wide?: boolean;
    children: ReactNode;
}) {
    return (
        <div className={cn('min-w-0', wide && 'col-span-2')}>
            <dt className="text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                {label}
            </dt>
            <dd className={cn('mt-0.5 text-[11.5px]', wide ? 'break-words' : 'truncate')}>
                {children}
            </dd>
        </div>
    );
}

/** Grupo de campos con su rótulo; se separa del siguiente con una línea tenue, sin marco. */
function FactSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section
            aria-label={title}
            className="border-t border-border/60 px-3 py-2.5 first:border-t-0"
        >
            <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {title}
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2">{children}</dl>
        </section>
    );
}

const CHIP = 'inline-flex items-center rounded-full border px-1.5 py-px text-[9.5px] font-semibold';

function financiersLabel(values: readonly string[]): string {
    if (values.length === 0) return 'Solo contado';
    return values
        .map((v) => labelOf(FINANCIER_OPTIONS, v))
        .sort((a, b) => a.localeCompare(b, 'es'))
        .join(', ');
}

function ProductFacts({ product, kind }: { product: CatalogProduct; kind: CatalogItemKind }) {
    const text = (value: string | null | undefined) => (value?.trim() ? value : '—');
    return (
        <>
            <FactSection title="Identificación">
                <Field label="ID externo">{text(product.externalId)}</Field>
                <Field label="SKU">
                    <span className="font-mono">{product.sku}</span>
                </Field>
                <Field label={kind === 'PRODUCT' ? 'Descripción' : 'Nombre'} wide>
                    {text(product.description)}
                </Field>
            </FactSection>
            {kind === 'PRODUCT' ? (
                <FactSection title="Equipo">
                    <Field label="Marca">{text(product.brand)}</Field>
                    <Field label="Modelo">{text(product.model)}</Field>
                    <Field label="Categoría">
                        {labelOf(PRODUCT_CATEGORY_OPTIONS, product.category)}
                    </Field>
                    <Field label="Gama">{labelOf(TIER_FORM_OPTIONS, product.tier)}</Field>
                    <Field label="Capacidad · color">{variantLabel(product) || '—'}</Field>
                    <Field label="Rastreo">{trackingLabel(product.trackingMode)}</Field>
                </FactSection>
            ) : null}
            <FactSection title="Venta">
                <Field label="Se vende suelto">{yesNo(product.sellable)}</Field>
                <Field label="En el flujo de venta">{yesNo(product.showInSalesFlow)}</Field>
                {kind === 'SERVICE' ? null : (
                    <Field label="Financieras" wide>
                        {financiersLabel(product.eligibleFinanciers)}
                    </Field>
                )}
                {kind === 'PRODUCT' && product.category === 'PHONE' ? (
                    <Field label="MDM">
                        {product.mdmEnabled
                            ? `${labelOf(MDM_PROVIDER_OPTIONS, product.mdmProvider)} · ${labelOf(MDM_LOCK_MODE_OPTIONS, product.mdmLockMode)}`
                            : 'Sin MDM'}
                    </Field>
                ) : null}
            </FactSection>
        </>
    );
}

function KitComponents({ product }: { product: CatalogProduct }) {
    const components = [...(product.components ?? [])].sort((a, b) => a.sku.localeCompare(b.sku));
    return (
        <section aria-label="Componentes" className="border-t border-border/60 px-3 py-2.5">
            <h3 className="mb-1.5 flex items-baseline justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Componentes</span>
                <span className="font-normal normal-case">{`${String(components.length)} SKU`}</span>
            </h3>
            <ul className="flex flex-col divide-y divide-border/40 text-[11px]">
                {components.map((c) => (
                    <li key={c.sku} className="flex items-center justify-between gap-2 py-1">
                        <span className="truncate font-mono">{c.sku}</span>
                        <span className="flex-none tabular-nums text-muted-foreground">
                            {`${String(c.qty)} por kit`}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function DeactivateAction({ product }: { product: CatalogProduct }) {
    const lock = useIntegrationLock();
    const locked = lock.locked && !isIntegrationFreeKind(itemTypeOf(product));
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const deactivate = useDeactivateProduct(product.sku);
    const attempt = useIdempotentAttempt(`deactivate:${product.sku}`);
    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[10.5px] text-danger"
                leftIcon={<Power className="size-3" aria-hidden />}
                disabled={locked}
                title={locked ? lock.reason : undefined}
                onClick={() => {
                    setError(null);
                    setOpen(true);
                }}
            >
                Desactivar
            </Button>
            <ConfirmDialog
                open={open}
                onOpenChange={setOpen}
                title={`Desactivar ${product.sku}`}
                description="Deja de venderse y sale del catálogo de venta. No se borra: su historial se conserva."
                confirmLabel="Desactivar"
                destructive
                loading={deactivate.isPending}
                onConfirm={() => {
                    setError(null);
                    attempt
                        .run(product.sku, (key) => deactivate.mutateAsync({ key }))
                        .then(() => {
                            toast.success('SKU desactivado', { description: product.sku });
                            setOpen(false);
                        })
                        .catch((failure: unknown) => {
                            const mapped = deactivateErrorOf(failure);
                            if (mapped.action === 'NEW_KEY') attempt.renew(product.sku);
                            setError(mapped.message);
                        });
                }}
            >
                <WriteErrorNotice message={error} />
            </ConfirmDialog>
        </>
    );
}

export function ProductTab({ sku, retailerId }: { sku: string; retailerId: string | null }) {
    const { canUpdateProduct } = useInventoryAbilities();
    const query = useCatalogProduct(retailerId, sku, true);
    const [editing, setEditing] = useState(false);
    if (retailerId === null) {
        return (
            <DetailEmpty>
                No podemos leer el producto sin una cadena asignada a tu usuario.
            </DetailEmpty>
        );
    }
    if (query.error) {
        return (
            <ErrorState
                title="No se pudo cargar el producto"
                description={skuDetailErrorMessage(query.error)}
                onRetry={() => {
                    void query.refetch();
                }}
            />
        );
    }
    const product = query.data;
    if (product === undefined) return <Skeleton lines={5} />;
    const kind = itemTypeOf(product);
    const active = product.status === ProductStatusEnum.ACTIVE;
    const actions = canUpdateProduct ? (
        <span className="flex gap-1.5">
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[10.5px]"
                leftIcon={<Pencil className="size-3" aria-hidden />}
                onClick={() => {
                    setEditing(true);
                }}
            >
                Editar
            </Button>
            {active ? <DeactivateAction product={product} /> : null}
        </span>
    ) : null;
    return (
        <>
            <section
                aria-label="Producto"
                className="mb-2 overflow-hidden rounded-md border border-border bg-card"
            >
                <div
                    data-testid="product-status"
                    className="flex flex-wrap items-center justify-between gap-2 bg-muted/30 px-3 py-1.5"
                >
                    <span className="flex items-center gap-1.5">
                        <span
                            className={cn(
                                CHIP,
                                active
                                    ? 'border-success-soft-border bg-success-soft text-success'
                                    : 'border-border bg-muted text-muted-foreground',
                            )}
                        >
                            {active ? 'Activo' : 'Inactivo'}
                        </span>
                        <span className={cn(CHIP, 'border-border bg-card text-muted-foreground')}>
                            {KIND_LABEL[kind]}
                        </span>
                    </span>
                    {actions}
                </div>
                <ProductFacts product={product} kind={kind} />
                {kind === 'KIT' ? <KitComponents product={product} /> : null}
            </section>
            {canUpdateProduct ? (
                <EditItemDrawer
                    product={product}
                    retailerId={retailerId}
                    isOpen={editing}
                    onClose={() => {
                        setEditing(false);
                    }}
                />
            ) : null}
        </>
    );
}
